import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { StyledTableRow } from "../../../../../../styles/Shipmentstyles";
import { InventoryPackSummary } from "../../../../../../models/inventory/InventoryAdmin";

interface PackagingOpsPacksTableProps {
  packs: InventoryPackSummary[];
  isLoading?: boolean;
  showActions?: boolean;
  onEditPack: (pack: InventoryPackSummary) => void;
  onEditPackItems: (pack: InventoryPackSummary) => void;
  onDeletePack: (pack: InventoryPackSummary) => void;
  onViewItems: (pack: InventoryPackSummary) => void;
}

const PackagingOpsPacksTable: React.FC<PackagingOpsPacksTableProps> = ({
  packs,
  isLoading = false,
  showActions = true,
  onEditPack,
  onEditPackItems,
  onDeletePack,
  onViewItems,
}) => {
  const theme = useTheme();
  const [rowActionAnchor, setRowActionAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [activePack, setActivePack] = useState<InventoryPackSummary | null>(
    null,
  );

  const handleOpenActions = (
    event: React.MouseEvent<HTMLElement>,
    pack: InventoryPackSummary,
  ) => {
    setRowActionAnchor(event.currentTarget);
    setActivePack(pack);
  };

  const handleCloseActions = () => {
    setRowActionAnchor(null);
    setActivePack(null);
  };

  return (
    <TableContainer sx={{ maxHeight: window.innerHeight - 360 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor:
                theme.palette.customColors?.tableGrayHeadingColor,
            }}
          >
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              S.NO
            </AppTableHeaderCell>
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              PACK
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              ITEMS
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              PLANNED QTY
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              BUILDABLE NOW
            </AppTableHeaderCell>
            {/* <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              IN FLIGHT
            </AppTableHeaderCell>
            <AppTableHeaderCell
              minWidth="50px"
              fontSize="12px"
              sx={{ textAlign: "right" }}
            >
              SHIPPED
            </AppTableHeaderCell> */}
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              STATUS
            </AppTableHeaderCell>
            {showActions && (
            <AppTableHeaderCell minWidth="50px" fontSize="12px">
              ACTION
            </AppTableHeaderCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!isLoading && packs.length === 0 && (
            <StyledTableRow>
              <TableCell colSpan={showActions ? 9 : 8}>
                <Box py={3} textAlign="center">
                  <CustomTypography variant="body2" color="text.secondary">
                    No packs found for this order.
                  </CustomTypography>
                </Box>
              </TableCell>
            </StyledTableRow>
          )}
          {packs.map((pack, index) => (
            <StyledTableRow key={pack.packId}>
              <AppTableRowCell minWidth="50px">
                <CustomTypography variant="body2">{index + 1}</CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px">
                <CustomTypography variant="body2" fontWeight={600}>
                  {pack.label}
                </CustomTypography>
                <CustomTypography variant="caption" color="text.secondary">
                  #{pack.packId}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell
                minWidth="50px"
                sx={{ textAlign: "right", cursor: "pointer" }}
                onClick={() => onViewItems(pack)}
              >
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  color={theme.palette.customColors?.blueGradientStart}
                  sx={{ textDecoration: "underline" }}
                >
                  {pack.productsCount}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <CustomTypography variant="body2">
                  {pack.plannedQty}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  color={
                    pack.buildableNow < 0
                      ? theme.palette.error.main
                      : theme.palette.customColors?.text
                  }
                >
                  {pack.buildableNow}
                </CustomTypography>
              </AppTableRowCell>
              {/* <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <CustomTypography variant="body2">
                  {pack.inFlight}
                </CustomTypography>
              </AppTableRowCell>
              <AppTableRowCell minWidth="50px" sx={{ textAlign: "right" }}>
                <CustomTypography variant="body2">
                  {pack.shipped}
                </CustomTypography>
              </AppTableRowCell> */}
              <AppTableRowCell minWidth="50px">
                <Chip
                  size="small"
                  label={pack.active === 1 ? "Active" : "Inactive"}
                  sx={{
                    backgroundColor:
                      pack.active === 1
                        ? theme.palette.customColors?.greenLight
                        : theme.palette.customColors?.grayBackGroundColor,
                    color: theme.palette.customColors?.text,
                    fontWeight: 600,
                  }}
                />
              </AppTableRowCell>
              {showActions && (
              <AppTableRowCell minWidth="50px">
                <Button
                  variant="outlined"
                  onClick={(event) => handleOpenActions(event, pack)}
                  sx={{
                    minWidth: "94px",
                    height: "32px",
                    fontSize: "0.8rem",
                    textTransform: "none",
                    borderColor: theme.palette.customColors?.greyBorder,
                    color: theme.palette.customColors?.text,
                  }}
                >
                  Actions
                </Button>
              </AppTableRowCell>
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
      <Menu
        anchorEl={rowActionAnchor}
        open={Boolean(rowActionAnchor)}
        onClose={handleCloseActions}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            if (activePack) onEditPack(activePack);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Edit Pack
          </CustomTypography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activePack) onEditPackItems(activePack);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Edit Items
          </CustomTypography>
        </MenuItem>
        <MenuItem
          disabled={!activePack || activePack.active !== 1}
          onClick={() => {
            if (activePack) onDeletePack(activePack);
            handleCloseActions();
          }}
        >
          <CustomTypography variant="body2" fontWeight={600}>
            Delete
          </CustomTypography>
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default observer(PackagingOpsPacksTable);
